// The calendar's Proposed sidebar (views/events/pitches/list.vue) feed.
//
// The sidebar used to fetch every proposed event in one request and refetch
// all of it on every EventsChannel broadcast. On a calendar with 8,828
// proposed events that was 17 MB and 17.5s per request, and a bulk delete of
// 25 events a second fired one full refetch per event from every open
// sidebar, which pegged every puma worker. Now the endpoint is paged and the
// live reload is coalesced.
//
// Extracted from the component (same reasoning as lib/singleFlightSave.mjs) so
// the paging and the coalescing can be driven in test/javascript without a
// browser.
import debounce from "lodash/debounce.js";

export const PITCHES_PAGE_SIZE = 30;
// Matches EventsController::PITCHES_MAX_PER_PAGE.
export const PITCHES_MAX_PER_PAGE = 200;

export const RELOAD_WAIT_MS = 1500;
// A steady stream of broadcasts (a long bulk delete) would keep pushing a
// plain trailing debounce back forever; maxWait still refreshes every few
// seconds while it runs.
export const RELOAD_MAX_WAIT_MS = 5000;

export function pitchesUrl({
  sort,
  calendarId,
  userId,
  categoryId,
  page = 1,
  perPage = PITCHES_PAGE_SIZE,
}) {
  let url = `/events.json?pitches=true&page=${page}&per_page=${perPage}`;
  if (sort && sort !== "submitted") {
    url += `&sort=events.starts_at ${sort}`;
  }
  if (calendarId) url += `&calendar_id=${calendarId}`;
  if (userId && userId !== "Everyone") url += `&user_id=${userId}`;
  if (categoryId && categoryId !== "All Categories") {
    url += `&event_category_id=${categoryId}`;
  }
  return url;
}

// A live reload refreshes every row already on screen in one request: the
// loaded count rounded up to whole pages, so "Load more" can keep asking for
// the next page by number afterwards.
export function reloadPageSize(loadedCount) {
  const pages = Math.max(1, Math.ceil(loadedCount / PITCHES_PAGE_SIZE));
  return Math.min(pages * PITCHES_PAGE_SIZE, PITCHES_MAX_PER_PAGE);
}

export function nextPageNumber(loadedCount) {
  return Math.floor(loadedCount / PITCHES_PAGE_SIZE) + 1;
}

// The endpoint sends each sender's avatar once per page rather than on every
// row. Put it back on the row: the row template, and FullCalendar when a row
// is dragged onto a day, both read pitch.user_gravatar.
export function withAvatars({ data, avatars = {} }) {
  return data.map((pitch) => ({
    ...pitch,
    user_gravatar: avatars[pitch.user_id] || null,
  }));
}

// Rows from a later page, minus any already shown. Rows shift between pages
// when a pitch is added or removed between requests.
export function appendPage(loaded, incoming) {
  const seen = new Set(loaded.map((pitch) => pitch.id));
  return loaded.concat(incoming.filter((pitch) => !seen.has(pitch.id)));
}

export function coalesceReloads(reload) {
  return debounce(reload, RELOAD_WAIT_MS, { maxWait: RELOAD_MAX_WAIT_MS });
}
